import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import ValueLoopDiagram from '../shared/ValueLoopDiagram'
import { getEnterpriseAIContent } from '../../constants/enterpriseAIContent'

const LoopAccordionItem = ({ step, index, selected, onSelect, panelId, tabId }) => {
  const innerRef = useRef(null)
  const [maxHeight, setMaxHeight] = useState(0)

  const measure = useCallback(() => {
    if (!innerRef.current) return
    setMaxHeight(selected ? innerRef.current.scrollHeight : 0)
  }, [selected])

  useLayoutEffect(() => {
    measure()
  }, [measure, step.body])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.ResizeObserver) return undefined

    const observer = new ResizeObserver(measure)
    if (innerRef.current) observer.observe(innerRef.current)

    return () => observer.disconnect()
  }, [measure])

  return (
    <div className={'enterprise-ai-loop__item' + (selected ? ' enterprise-ai-loop__item--open' : '')}>
      <button
        type="button"
        role="tab"
        id={tabId}
        className="enterprise-ai-loop__trigger"
        aria-selected={selected}
        aria-expanded={selected}
        aria-controls={panelId}
        tabIndex={selected ? 0 : -1}
        onClick={() => onSelect(index)}
      >
        <span className="enterprise-ai-loop__dots" aria-hidden="true">
          {Array.from({ length: index + 1 }).map((_, dotIndex) => (
            <i key={dotIndex} />
          ))}
        </span>
        <span className="enterprise-ai-loop__step-title">
          {index + 1}. {step.title}
        </span>
      </button>
      <div
        className="enterprise-ai-loop__panel-wrap"
        style={{ maxHeight: selected ? maxHeight : 0 }}
        aria-hidden={!selected}
      >
        <div
          ref={innerRef}
          role="tabpanel"
          id={panelId}
          className="enterprise-ai-loop__panel"
          aria-labelledby={tabId}
        >
          <p>{step.body}</p>
        </div>
      </div>
    </div>
  )
}

const AIValueLoop = ({ lang }) => {
  const { loop } = getEnterpriseAIContent(lang)
  const defaultIndex = loop.steps.findIndex(s => s.id === loop.defaultStepId)
  const [activeIndex, setActiveIndex] = useState(defaultIndex >= 0 ? defaultIndex : 0)

  const selectStep = useCallback(index => {
    setActiveIndex(index)
  }, [])

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        selectStep((activeIndex + 1) % loop.steps.length)
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        selectStep((activeIndex - 1 + loop.steps.length) % loop.steps.length)
      }
    },
    [activeIndex, loop.steps.length, selectStep]
  )

  return (
    <section
      className="enterprise-ai-loop"
      id="enterprise-ai-loop"
      aria-labelledby="enterprise-ai-loop-heading"
    >
      <div className="content container">
        <div className="enterprise-ai-pillars__head">
          <h2 id="enterprise-ai-loop-heading" className="enterprise-ai-pillars__heading">
            {loop.heading}
          </h2>
          <p className="enterprise-ai-pillars__lede">
            <span className="enterprise-ai-pillars__lede-accent">{loop.ledeAccent}</span>
            {loop.lede}
          </p>
        </div>
        <div className="enterprise-ai-loop__layout">
          <ValueLoopDiagram
            variant="accent"
            className="enterprise-ai-loop__diagram"
            steps={loop.steps}
            centerLabel={loop.centerLabel}
            activeIndex={activeIndex}
            interactive
            onHoverSelect={selectStep}
            onSelect={selectStep}
          />
          <div
            className="enterprise-ai-loop__accordion"
            role="tablist"
            aria-label={loop.heading}
            onKeyDown={onKeyDown}
          >
            {loop.steps.map((step, index) => (
              <LoopAccordionItem
                key={step.id}
                step={step}
                index={index}
                selected={index === activeIndex}
                onSelect={selectStep}
                tabId={`enterprise-ai-loop-tab-${step.id}`}
                panelId={`enterprise-ai-loop-panel-${step.id}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default AIValueLoop
